/* ===== Beat maps (GM Studio package 6) =====
 *
 * A beat has an ordered, explicit set of maps (`pageIds`) and an optional
 * primary map (`pageId`) — one of the set, or null. The primary is the
 * default target of «open the beat's map»; it is never shown to players by
 * itself. Invariant: `pageId === null || pageIds.includes(pageId)`. Only the
 * server's beat commands change the pair, both in one operation; tabs, open
 * panes and location snapshots never do.
 *
 * Before v45 a beat had one map, `pageId`. `normalizeBeatMaps` turns that
 * into the set of that map + the primary ONCE (SessionManager.loadCampaign,
 * persisted). A beat that already has `pageIds` is never rebuilt from
 * `pageId`, so a link the GM removed does not come back. Whether a map still
 * exists is not consulted: a deleted map's id stays a visible «deleted» link
 * until the GM removes it — nothing is created, nothing is shown to players.
 */

import type { Beat } from './domain.js';

type BeatMapsShape = { readonly pageId?: unknown; readonly pageIds?: unknown };

const isId = (value: unknown): value is string => typeof value === 'string' && value !== '';

/** The beat's maps in order, readable from the pre-v45 shape as well. */
export function beatMapIds(beat: BeatMapsShape): readonly string[] {
  if (Array.isArray(beat.pageIds)) return beat.pageIds.filter(isId);
  return isId(beat.pageId) ? [beat.pageId] : [];
}

/** The beat's primary map (null — none), readable from the pre-v45 shape as well. */
export function beatPrimaryMapId(beat: BeatMapsShape): string | null {
  return isId(beat.pageId) ? beat.pageId : null;
}

/**
 * The beat with a valid map pair (N1–N5, package 6 contract §4.3), or the
 * same object when nothing has to change:
 *  N1 no `pageIds` — the old single map becomes the set and the primary;
 *  N2 `pageIds` kept as is, minus non-ids and repeats (order preserved);
 *  N3 a primary that is not an id → null;
 *  N4 a primary outside the set (written by a pre-v45 server) is appended;
 *  N5 map existence is not consulted.
 */
export function normalizeBeatMaps<B extends { pageId?: string | null; pageIds?: string[] }>(
  beat: B,
): B & Pick<Beat, 'pageId' | 'pageIds'> {
  const primary: string | null = isId(beat.pageId) ? beat.pageId : null;
  if (!Array.isArray(beat.pageIds)) {
    return { ...beat, pageIds: primary === null ? [] : [primary], pageId: primary };
  }
  const ids: string[] = [];
  for (const id of beat.pageIds as unknown[]) {
    if (isId(id) && !ids.includes(id)) ids.push(id);
  }
  if (primary !== null && !ids.includes(primary)) ids.push(primary);
  const same =
    beat.pageId === primary &&
    ids.length === beat.pageIds.length &&
    ids.every((id, i) => id === beat.pageIds![i]);
  return same
    ? (beat as B & Pick<Beat, 'pageId' | 'pageIds'>)
    : { ...beat, pageIds: ids, pageId: primary };
}

/** Normalize every beat; the same array contents and changed=false when nothing changed. */
export function normalizeAllBeatMaps<B extends { pageId?: string | null; pageIds?: string[] }>(
  beats: readonly B[],
): { readonly beats: Array<B & Pick<Beat, 'pageId' | 'pageIds'>>; readonly changed: boolean } {
  let changed = false;
  const next = beats.map((beat) => {
    const normalized = normalizeBeatMaps(beat);
    if (normalized !== beat) changed = true;
    return normalized;
  });
  return { beats: next, changed };
}
